import { GetDataIndex } from 'features/conge/services/Timeline';
import { CongeType, FiltersTimeline } from 'features/conge/types/timeline';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

interface TypeCongeProps {
  selectedTypes: number[];
  setSelectedTypes: Dispatch<SetStateAction<number[]>>;
  setFilters: Dispatch<SetStateAction<FiltersTimeline>>;
}

const TypeCongeSkeleton: FC = () => {
  return (
    <div className="minthr-flex minthr-flex-items-center minthr-mobile-ml-0 minthr-mobile-mb-4">
      <style>{`
        .skeleton-loader {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
          border-radius: calc(var(--unit-minthr) * 4);
        }

        @keyframes skeleton-loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>

      <div className="minthr-flex minthr-flex-items-center minthr-overflow-hidden minthr-type-conge">
        {[...Array(10)].map((_, index) => (
          <div key={index} className="minthr-flex-shrink-0">
            <div
              className="skeleton-loader text-s minthr-text-nowrap minthr-mr-2 minthr-mobile-mr-1 minthr-badge minthr-bg-white minthr-py-0 minthr-flex minthr-flex-items-center"
              style={{
                height: 'calc(var(--unit-minthr) * 24)',
                width: `calc(var(--unit-minthr) * ${120 + Math.random() * 40})`,
                padding: '0 calc(var(--unit-minthr) * 12)',
              }}
            ></div>
          </div>
        ))}
      </div>

      <div className="minthr-flex minthr-flex-items-center minthr-ml-4">
        <div
          className="skeleton-loader minthr-type-conge-prev"
          style={{
            width: 'calc(var(--unit-minthr) * 32)',
            height: 'calc(var(--unit-minthr) * 32)',
            borderRadius: 'calc(var(--unit-minthr) * 4)',
          }}
        ></div>
        <div
          className="skeleton-loader minthr-ml-2 minthr-type-conge-next"
          style={{
            width: 'calc(var(--unit-minthr) * 32)',
            height: 'calc(var(--unit-minthr) * 32)',
            borderRadius: 'calc(var(--unit-minthr) * 4)',
          }}
        ></div>
      </div>
    </div>
  );
};

const TypeConge: FC<TypeCongeProps> = ({
  selectedTypes,
  setSelectedTypes,
  setFilters,
}) => {
  const [typeConge, setTypeConge] = useState<CongeType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollAmount = 100;

  useEffect(() => {
    const fetchTypeConge = async () => {
      setIsLoading(true);
      const { data } = await GetDataIndex();
      setTypeConge((prev) => [...prev, ...data.conge]);
      setIsLoading(false);
    };

    fetchTypeConge();
  }, []);

  const handleScroll = (direction: 'left' | 'right'): void => {
    const container = scrollContainerRef.current;

    if (container) {
      if (direction === 'left') {
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else if (direction === 'right') {
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    }
  };

  const handleSelectedType = (id: number): void => {
    setSelectedTypes((prev) => {
      const updatedTypes = prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id];

      setFilters((prevState: FiltersTimeline) => ({
        ...prevState,
        filter: prevState.filter.map((f) =>
          f.filterName === 'TypeConge'
            ? { ...f, filterValue: updatedTypes.join(',') }
            : f,
        ),
      }));

      return updatedTypes;
    });
  };

  if (isLoading) {
    return <TypeCongeSkeleton />;
  }

  return (
    <div className="minthr-flex minthr-flex-items-center minthr-mobile-ml-0 minthr-mobile-mb-4">
      <div
        className="minthr-flex minthr-flex-items-center minthr-overflow-hidden minthr-type-conge"
        ref={scrollContainerRef}
      >
        {typeConge.map((item: CongeType) => (
          <div key={item.id} className="minthr-flex-shrink-0">
            <label
              onClick={() => handleSelectedType(item.id)}
              className={`text-s minthr-text-nowrap minthr-mr-2 minthr-mobile-mr-1 minthr-color-dark minthr-badge-tab-primary minthr-badge minthr-bg-white minthr-py-0 minthr-flex minthr-flex-items-center ${selectedTypes.includes(item.id) ? 'active' : ''}`}
            >
              <span className="h2 minthr-mr-1" style={{ color: item.color }}>
                ●
              </span>
              {item.title}
            </label>
          </div>
        ))}
      </div>

      <div className="minthr-flex minthr-flex-items-center minthr-ml-4">
        <button
          type="button"
          className="minthr-btn minthr-btn-secondary minthr-p-0 minthr-type-conge-prev"
          onClick={() => handleScroll('left')}
        >
          <img
            className="minthr-icon-s minthr-icon-primary minthr-icon-reverse"
            src="/icons/arrow.svg"
            alt="Scroll Left"
          />
        </button>
        <button
          type="button"
          className="minthr-btn minthr-btn-secondary minthr-p-0 minthr-ml-2 minthr-type-conge-next"
          onClick={() => handleScroll('right')}
        >
          <img
            className="minthr-icon-s minthr-icon-primary"
            src="/icons/arrow.svg"
            alt="Scroll Right"
          />
        </button>
      </div>
    </div>
  );
};

export default TypeConge;
